import { AsyncPipe, DecimalPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject, Input, OnInit, TemplateRef, ViewChild } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { Chart, ChartConfiguration, Plugin } from 'chart.js'
import { BaseChartDirective } from 'ng2-charts'
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, map, Observable, ReplaySubject, shareReplay, switchMap, tap } from 'rxjs'
import { ButtonToggleComponent, ButtonToggleOption } from '../common/button-toggle/button-toggle.component'
import { convertIntervalKeyToTimeInterval, IntervalKey, TimeIntervalComponent } from '../common/time-interval/time-interval.component'
import { getActiveToggleMenuOptionIds } from '../common/toggle-menu/toggle-menu.component'
import { constructQueryParameterName, getPaginationOptionsFromQueryParamMap } from '../list-query-options'
import { MeetingRoomMetric, MetricsService, UsageByOrganizationMetric, UsageByUserMetric } from '../metrics.service'
import { AppTableColumn, TableComponent } from '../table/table.component'
import { SupportLinkResolver } from '../multi-environment/support-link.resolver'
import { SupportLinkComponent } from '../multi-environment/support-link/support-link.component'
import { SpinnerComponent } from '../spinner/spinner.component'
import { constructNameForUser, getColorPalette, isEqual } from '../utils'
import { MatButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { MatSlideToggle } from '@angular/material/slide-toggle'
import DataLabelsPlugin from 'chartjs-plugin-datalabels'
import annotationPlugin from 'chartjs-plugin-annotation'
import { AUDIO_QUALITY_CHART_DATA, createDotsPlugin } from './audio-quality-chart.utils'

Chart.register(annotationPlugin)

@Component({
  selector: 'app-analytics-dashboard',
  standalone: true,
  imports: [
    TimeIntervalComponent,
    AsyncPipe,
    TranslatePipe,
    BaseChartDirective,
    SpinnerComponent,
    TableComponent,
    SupportLinkComponent,
    ButtonToggleComponent,
    DecimalPipe,
    MatButton,
    MatIcon,
    MatSlideToggle,
  ],
  templateUrl: './analytics-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './analytics-dashboard.component.scss',
})
export class AnalyticsDashboardComponent implements OnInit {
  @Input({ required: true })
  organizationIds$!: Observable<number[]>

  @Input()
  dashboardUrl: string | null = null

  @Input()
  showAudioMetrics = false

  @Input()
  showMeetingRoomMetrics = false

  @Input()
  showDetailedAudioMetrics = false

  @Input()
  showMeetingNameToggle = false

  dataLabelsPlugin: Plugin<'bar'> = DataLabelsPlugin as Plugin<'bar'>

  showMeetingLabels$ = new BehaviorSubject<boolean>(false)
  showMeetingRoomLabels$ = new BehaviorSubject<boolean>(false)

  toggleMeetingLabels() {
    this.showMeetingLabels$.next(!this.showMeetingLabels$.value)
  }

  toggleMeetingRoomLabels() {
    this.showMeetingRoomLabels$.next(!this.showMeetingRoomLabels$.value)
  }

  ngOnInit() {
    this.organizationIds$.subscribe((value) => this._organizationIds$.next(value))
  }

  _organizationIds$ = new ReplaySubject<number[]>(1)
  route = inject(ActivatedRoute)
  router = inject(Router)
  metricsService = inject(MetricsService)
  translateService = inject(TranslateService)
  completedCallQueryPrefix = 'completedCalls'
  usageStatsQueryPrefix = 'usageStats'
  defaultTimeInterval = 'LAST_6_MONTHS' as IntervalKey
  completedCallsDefaultPageSize = 25

  selectedInterval$ = this.route.queryParamMap.pipe(map((queryParamMap) => (queryParamMap.get('intervalKey') ?? this.defaultTimeInterval) as IntervalKey))

  interval$ = this.route.queryParamMap.pipe(
    map((params) => (params.get('intervalKey') as IntervalKey) ?? this.defaultTimeInterval),
    distinctUntilChanged(),
    map(convertIntervalKeyToTimeInterval),
    shareReplay(1)
  )

  usagesByOrganization$ = combineLatest([this._organizationIds$, this.interval$]).pipe(
    debounceTime(300),
    switchMap(([ids, interval]) => this.metricsService.getUsageByOrganizationMetrics(ids, interval.from)),
    shareReplay(1)
  )

  usageSummary$ = this.usagesByOrganization$.pipe(
    map((result) => {
      const organizations = result.data ?? []
      const totals = organizations
        .flatMap((organization) => organization.usageByInterval)
        .reduce(
          (acc, interval) => ({
            duration: acc.duration + (interval.duration ?? 0),
            count: acc.count + (interval.count ?? 0),
          }),
          { duration: 0, count: 0 }
        )
      return {
        isLoading: result.isLoading,
        duration: totals.duration,
        count: totals.count,
        activeUserCount: organizations.reduce((acc, organization) => acc + organization.activeUserCount, 0),
      }
    }),
    shareReplay(1)
  )
  private usageStatsRequestOptions$ = this.route.queryParamMap.pipe(
    map((queryParamMap) => ({
      intervalKey: (queryParamMap.get('intervalKey') as IntervalKey) ?? this.defaultTimeInterval,
    })),
    distinctUntilChanged(isEqual),
    shareReplay(1)
  )

  userStats$ = combineLatest([this.usageStatsRequestOptions$, this._organizationIds$]).pipe(
    map(([requestOptions, organizationIds]) => ({ ...requestOptions, organizationIds })),
    distinctUntilChanged(isEqual),
    switchMap(({ intervalKey, organizationIds }) =>
      this.metricsService.getUsageByUserMetrics('userStats', null, organizationIds, [], null, convertIntervalKeyToTimeInterval(intervalKey).from)
    ),
    shareReplay(1)
  )

  usageStatsSelectedView$ = this.route.queryParamMap.pipe(
    map((queryParamMap) => queryParamMap.get(constructQueryParameterName('activeView', this.usageStatsQueryPrefix)) ?? 'HOURS'),
    distinctUntilChanged(),
    tap((view) => console.log('Usage stats view changed to', view)),
    shareReplay(1)
  )

  /**
   * One column per interval, named after the interval itself rather than by a translation key, and
   * shown in whichever unit the view is set to.
   */
  columnDefinitionsForUserStats$ = combineLatest([this.userStats$, this.usageStatsSelectedView$, this._organizationIds$]).pipe(
    map(([result, viewMode, organizationIds]): AppTableColumn<UsageByUserMetric>[] => {
      const usageData = result.data ?? []
      if (usageData.length === 0) {
        return []
      }

      const readUsage = (value: number | null): number => (value === null ? -1 : viewMode === 'HOURS' ? value / 3600 : value)
      const showUsage = (value: number | null): string => {
        if (value === null) {
          return 'N/A'
        }
        if (value === 0) {
          return '-'
        }
        return viewMode === 'HOURS' ? (value / 3600).toFixed(1) : String(value)
      }

      const name: AppTableColumn<UsageByUserMetric> = {
        id: 'name',
        header: 'COMMON.USER',
        value: (metricDataPoint) => constructNameForUser(metricDataPoint.user),
        tone: 'strong',
        sortable: true,
      }

      const organization: AppTableColumn<UsageByUserMetric> = {
        id: 'organizationId',
        header: 'COMMON.ORGANIZATION',
        value: (metricDataPoint) => metricDataPoint.user.organization?.name ?? '',
        cellTemplate: this.organizationCell,
        filter: 'facet',
      }

      const status: AppTableColumn<UsageByUserMetric> = {
        id: 'status',
        header: 'COMMON.STATUS',
        value: (metricDataPoint) => (metricDataPoint.active ? 'ACTIVE' : 'INACTIVE'),
        chip: (metricDataPoint) => ({
          label: this.translateService.instant(metricDataPoint.active ? 'COMMON.ACTIVE' : 'COMMON.INACTIVE'),
          variant: metricDataPoint.active ? 'success' : 'neutral',
        }),
        filter: 'facet',
        facetLabel: (value) => this.translateService.instant(value === 'ACTIVE' ? 'COMMON.ACTIVE' : 'COMMON.INACTIVE'),
        defaultFacetValues: ['ACTIVE'],
        facetSearch: false,
      }

      const intervalColumns = usageData[0].usageByInterval.map(({ intervalLabel, intervalType }, index: number) => ({
        id: `interval${index}`,
        header: () => this.formatIntervalLabel(intervalLabel, intervalType),
        value: (metricDataPoint: UsageByUserMetric) =>
          readUsage(viewMode === 'HOURS' ? metricDataPoint.usageByInterval[index].duration : metricDataPoint.usageByInterval[index].count),
        display: (metricDataPoint: UsageByUserMetric) =>
          showUsage(viewMode === 'HOURS' ? metricDataPoint.usageByInterval[index].duration : metricDataPoint.usageByInterval[index].count),
        numeric: true,
        sortable: true,
      }))

      const total: AppTableColumn<UsageByUserMetric> = {
        id: 'usageNumberTotal',
        header: 'COMMON.TOTAL',
        value: (metricDataPoint) => readUsage(viewMode === 'HOURS' ? metricDataPoint.totalDuration : metricDataPoint.totalCount),
        display: (metricDataPoint) => showUsage(viewMode === 'HOURS' ? metricDataPoint.totalDuration : metricDataPoint.totalCount),
        numeric: true,
        sortable: true,
      }

      return [name, ...(organizationIds.length > 1 ? [organization] : []), status, ...intervalColumns, total]
    })
  )

  userStatsRowId = (metricDataPoint: UsageByUserMetric): string => String(metricDataPoint.user.id)

  @ViewChild('organizationCell', { static: true }) organizationCell!: TemplateRef<{ $implicit: UsageByUserMetric }>

  canFollowSupportLinks$ = inject(SupportLinkResolver).canFollowSupportLinks()

  // Spread around the hue circle by the golden angle, so neighbouring ids do not come out alike.
  organizationColor(organizationId: number): string {
    return `hsl(${(organizationId * 137.508) % 360}, 70%, 90%)`
  }

  exclusionFiltersForCompletedCalls$ = this.route.queryParamMap.pipe(
    map((queryParamMap) => {
      return {
        ERRORED: !(queryParamMap.get(constructQueryParameterName('exclusionFilters', this.completedCallQueryPrefix, 'ERRORED')) === 'false'),
        SHORTER_THAN_15_MIN:
          queryParamMap.get(constructQueryParameterName('exclusionFilters', this.completedCallQueryPrefix, 'SHORTER_THAN_15_MIN')) === 'true',
      }
    })
  )

  completedCalls$ = combineLatest([this._organizationIds$, this.route.queryParamMap, this.exclusionFiltersForCompletedCalls$]).pipe(
    debounceTime(300),
    map(([organizationIds, queryParamMap, exclusionFiltersRaw]) => {
      const activeViewQueryParam = constructQueryParameterName('activeView', this.completedCallQueryPrefix)
      const nameQueryParam = constructQueryParameterName('name', this.completedCallQueryPrefix)
      return {
        organizationIds,
        exclusionFilters: getActiveToggleMenuOptionIds(exclusionFiltersRaw),
        activeView: queryParamMap.has(activeViewQueryParam) ? queryParamMap.get(activeViewQueryParam) : null,
        intervalKey: (queryParamMap.get('intervalKey') as IntervalKey) ?? this.defaultTimeInterval,
        callNameQuery: queryParamMap.has(nameQueryParam) ? queryParamMap.get(nameQueryParam) : null,
        ...getPaginationOptionsFromQueryParamMap(queryParamMap, this.completedCallQueryPrefix, {
          pageSize: this.completedCallsDefaultPageSize,
          sortField: 'actualStartDate',
          sortDirection: 'desc',
        }),
      }
    }),
    // See userStats$: the request is spelled out before the dedupe so that a query parameter it has
    // nothing to do with — the table's own sorting among them — cannot re-run it.
    distinctUntilChanged((previous, current) => isEqual(previous, current)),
    switchMap((request) =>
      this.metricsService.getCompletedCalls(
        null,
        request.organizationIds,
        request.exclusionFilters,
        request.activeView,
        convertIntervalKeyToTimeInterval(request.intervalKey),
        request.callNameQuery,
        request.sortField,
        request.sortDirection ?? 'desc',
        request.page,
        request.pageSize
      )
    )
  )

  meetingRoomMetrics$ = this._organizationIds$.pipe(switchMap((organizationIds) => this.metricsService.getMeetingRoomMetrics(organizationIds)))

  audioQualityChartData = AUDIO_QUALITY_CHART_DATA

  meetingRoomScoresChart$ = combineLatest([this.meetingRoomMetrics$, this.usagesByOrganization$, this.showMeetingRoomLabels$]).pipe(
    map(([result, usagesResult, showLabels]) => {
      const rooms = (result.data ?? []).filter((room) => room.averageConfidenceScore !== null)
      const orgs = usagesResult.data ?? []
      const colorPalette = getColorPalette(orgs.length)
      const orgColorMap = Object.fromEntries(orgs.map((org, i) => [org.organization.id, colorPalette[i]]))

      const dots = rooms.map((room) => ({
        name: room.canonicalName,
        score: room.averageConfidenceScore!,
        color: orgColorMap[room.organizationId] ?? '#B51800',
      }))

      const legend =
        orgs.length > 1
          ? orgs.map((org, i) => {
              const orgRooms = rooms.filter((r) => r.organizationId === org.organization.id)
              const avg = orgRooms.length > 0 ? orgRooms.reduce((sum, r) => sum + r.averageConfidenceScore!, 0) / orgRooms.length : null
              const avgLabel = this.translateService.instant('METRICS.ANALYTICS_DASHBOARD.AVG_AUDIO_SCORE')
              const label = avg !== null ? `${org.organization.name} (${avgLabel}: ${avg.toFixed(2)})` : org.organization.name
              return { name: label, color: colorPalette[i] }
            })
          : []

      return {
        options: this.buildAudioQualityChartOptions(),
        dotsPlugin: createDotsPlugin(dots, { showLabels }),
        legend,
      }
    }),
    shareReplay(1)
  )

  meetingScoresChart$ = combineLatest([this.completedCalls$, this.usagesByOrganization$, this.showMeetingLabels$]).pipe(
    map(([callsResult, usagesResult, showLabels]) => {
      const calls = (callsResult.data?.list ?? []).filter((call) => call.avgConfidenceScore !== null)
      const orgs = usagesResult.data ?? []
      const colorPalette = getColorPalette(orgs.length)
      const orgColorMap = Object.fromEntries(orgs.map((org, i) => [org.organization.id, colorPalette[i]]))

      const dots = calls.map((call) => ({
        name: call.name,
        score: call.avgConfidenceScore!,
        color: orgColorMap[call.organizationId] ?? '#B51800',
      }))

      const legend =
        orgs.length > 1
          ? orgs.map((org, i) => {
              const orgCalls = calls.filter((c) => c.organizationId === org.organization.id)
              const avg = orgCalls.length > 0 ? orgCalls.reduce((sum, c) => sum + c.avgConfidenceScore!, 0) / orgCalls.length : null
              const avgLabel = this.translateService.instant('METRICS.ANALYTICS_DASHBOARD.AVG_AUDIO_SCORE')
              const label = avg !== null ? `${org.organization.name} (${avgLabel}: ${avg.toFixed(2)})` : org.organization.name
              return { name: label, color: colorPalette[i] }
            })
          : []

      return {
        options: this.buildAudioQualityChartOptions(),
        dotsPlugin: createDotsPlugin(dots, { dotRadius: 5, showLabels }),
        legend,
      }
    }),
    shareReplay(1)
  )

  private buildAudioQualityChartOptions(): ChartConfiguration<'bar'>['options'] {
    const thresholdAnnotation = {
      type: 'line' as const,
      xMin: 5.5,
      xMax: 5.5,
      borderColor: '#017101',
      borderWidth: 3,
      borderDash: [6, 4],
      label: {
        display: true,
        content: this.translateService.instant('METRICS.ANALYTICS_DASHBOARD.SATISFACTORY_THRESHOLD'),
        position: 'start' as const,
        backgroundColor: 'transparent',
        color: '#017101',
        font: { size: 14 },
        yAdjust: -400,
        xAdjust: 140,
      },
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 80,
          left: 20,
          right: 20,
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: this.translateService.instant('METRICS.ANALYTICS_DASHBOARD.AUDIO_QUALITY_SCORE_AXIS'),
            font: { size: 15 },
            color: '#666',
          },
          grid: { display: false },
          offset: true,
          ticks: {
            font: { size: 12 },
            color: '#666',
            labelOffset: -50,
          },
        },
        y: {
          grid: {
            display: true,
            color: '#E5E7EB',
            drawTicks: false,
            drawOnChartArea: true,
          },
          border: {
            display: false,
          },
          ticks: {
            display: false,
            maxTicksLimit: 4,
          },
          beginAtZero: true,
        },
        yRight: {
          position: 'right' as const,
          title: {
            display: true,
            text: this.translateService.instant('METRICS.ANALYTICS_DASHBOARD.NUMBER_OF_CALLS_AXIS'),
            font: { size: 15 },
            color: '#666',
          },
          ticks: { display: false },
          grid: { display: false },
          border: {
            display: false,
          },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        datalabels: { display: false },
        annotation: {
          annotations: {
            threshold: thresholdAnnotation,
          },
        },
      },
    }
  }

  chartDataForOrganizationUsage$ = combineLatest([this.usagesByOrganization$, this.usageStatsSelectedView$]).pipe(
    map(([result, viewOption]) => {
      if (!result?.data) {
        return { labels: [], datasets: [] }
      }
      return this.transformToChartData(result.data, viewOption === 'HOURS' ? 'HOURS' : 'COUNTS')
    }),
    shareReplay(1)
  )

  barChartOptions$: Observable<ChartConfiguration<'bar'>['options']> = this._organizationIds$.pipe(
    map((organizationIds) => {
      const showLegend = organizationIds.length > 1

      return {
        responsive: true,
        layout: {
          padding: {
            top: 20,
          },
        },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
          },
          y: { stacked: true },
        },
        plugins: {
          legend: {
            display: showLegend,
            position: 'bottom',
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                const label = context.dataset.label ?? ''
                const value = context.raw
                return `${label}: ${Math.round(value as number)}`
              },
            },
          },
          datalabels: {
            anchor: 'end',
            align: 'top',
            color: '#000000',
            font: {
              weight: 'bold',
              size: 12,
            },
            formatter: (value: number, ctx) => {
              const datasets = ctx.chart.data.datasets
              let total = 0
              datasets.forEach((d) => {
                total += (d.data[ctx.dataIndex] as number) || 0
              })

              return ctx.datasetIndex === datasets.length - 1 && total > 0 ? total.toFixed(0) : ''
            },
          },
        },
      } satisfies ChartConfiguration<'bar'>['options']
    }),
    shareReplay(1)
  )

  usageStatsViewOptions: ButtonToggleOption[] = [
    {
      id: 'HOURS',
      labelTranslationKey: 'ADMIN.VIEW_OPTIONS.HOURS',
    },
    {
      id: 'COUNTS',
      labelTranslationKey: 'ADMIN.VIEW_OPTIONS.COUNTS',
    },
  ]

  updateSelectedView(value: string | null, prefix: string) {
    this.router.navigate([], {
      queryParams: {
        [constructQueryParameterName('activeView', prefix)]: value,
      },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    })
  }

  readonly meetingRoomColumns: AppTableColumn<MeetingRoomMetric>[] = [
    { id: 'canonicalName', header: 'METRICS.ANALYTICS_DASHBOARD.ROOM_NAME', value: (room) => room.canonicalName, tone: 'strong', sortable: true },
    {
      id: 'averageConfidenceScore',
      header: 'METRICS.ANALYTICS_DASHBOARD.CONFIDENCE_SCORE',
      value: (room) => room.averageConfidenceScore ?? -1,
      display: (room) => room.averageConfidenceScore?.toFixed(2) ?? '-',
      numeric: true,
      sortable: true,
    },
    {
      id: 'averageRms',
      header: 'METRICS.ANALYTICS_DASHBOARD.RMS',
      value: (room) => room.averageRms ?? -1,
      display: (room) => room.averageRms?.toFixed(2) ?? '-',
      numeric: true,
      sortable: true,
    },
    {
      id: 'averageHfRatio',
      header: 'METRICS.ANALYTICS_DASHBOARD.HF_RATIO',
      value: (room) => room.averageHfRatio ?? -1,
      display: (room) => room.averageHfRatio?.toFixed(2) ?? '-',
      numeric: true,
      sortable: true,
    },
    { id: 'callCount', header: 'METRICS.ANALYTICS_DASHBOARD.CALL_COUNT', value: (room) => room.callCount, numeric: true, sortable: true },
  ]

  meetingRoomRowId = (room: MeetingRoomMetric): string => room.canonicalName

  transformToChartData(usages: UsageByOrganizationMetric[], viewOption: 'HOURS' | 'COUNTS'): ChartConfiguration<'bar'>['data'] {
    const labels =
      usages[0]?.usageByInterval.map((usage) => {
        return this.formatIntervalLabel(usage.intervalLabel, usage.intervalType)
      }) ?? []
    const colorPalette = getColorPalette(usages.length)

    const datasets = usages.map((usage, index) => ({
      label: usage.organization.name,
      data: usage.usageByInterval.map((item) => (viewOption === 'HOURS' && item.duration !== null ? item.duration / 3600 : item.count)),
      backgroundColor: colorPalette[index],
    }))
    return {
      labels,
      datasets,
    }
  }

  updateIntervalKey(intervalKey: IntervalKey) {
    this.router.navigate([], {
      queryParams: { ['intervalKey']: intervalKey },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    })
  }

  formatIntervalLabel(intervalLabel: string, intervalType: 'DAY' | 'MONTH'): string {
    if (intervalType === 'MONTH') {
      // intervalLabel is "YYYY-MM"
      const date = new Date(`${intervalLabel}-01`)
      const month = date.toLocaleString('en-US', { month: 'long' }).toUpperCase() // "SEPTEMBER"
      return this.translateService.instant(`COMMON.MONTHS.${month}`)
    }

    if (intervalType === 'DAY') {
      // intervalLabel is "YYYY-MM-DD"
      const date = new Date(intervalLabel)
      return new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
      }).format(date)
    }

    return intervalLabel
  }
}
